import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';

interface ProgressBar {
  index: number;
  completed: boolean;
  animating: boolean;
  growClass: string | null;
  isCenter: boolean;
}

@Component({
  selector: 'app-angular-progress-bar',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './angular-progress-bar.component.html',
  styleUrls: ['./angular-progress-bar.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AngularProgressBarComponent {
  // Inputs configurables
  readonly totalBars = input<number>(24);
  readonly progress = input<number>(75);
  readonly colorCompleted = input<string>('#ffffff');
  readonly colorIncomplete = input<string>('#2C3030');
  readonly showPercentage = input<boolean>(false);
  
  // Constantes
  private readonly GROW_BARS_COUNT = 4;
  
  // Signal interno para animación
  private animatedBars = signal<Set<number>>(new Set());
  
  // Computed para generar las barras
  readonly bars = computed<ProgressBar[]>(() => {
    const total = this.totalBars();
    const percentage = Math.max(0, Math.min(100, this.progress()));
    const completedBarsCount = Math.round((percentage / 100) * total);
    const animated = this.animatedBars();
    
    // La barra central es la última completada (índice completedBarsCount - 1)
    const centerBarIndex = completedBarsCount - 1;
    
    return Array.from({ length: total }, (_, i) => {
      const isCompleted = i < completedBarsCount;
      
      // Calcular la distancia desde la barra central
      const distanceFromCenter = Math.abs(i - centerBarIndex);
      
      let growClass: string | null = null;
      
      // Aplicar grow de forma simétrica a ambos lados de la barra central
      if (distanceFromCenter < this.GROW_BARS_COUNT && centerBarIndex >= 0) {
        // grow-4 es la más alta (centro), grow-1 es la más baja del efecto
        const growLevel = this.GROW_BARS_COUNT - distanceFromCenter;
        growClass = `grow-${growLevel}`;
      }
      
      return {
        index: i,
        completed: isCompleted,
        animating: animated.has(i),
        growClass,
        isCenter: i === centerBarIndex
      };
    });
  });
  
  // Computed para el porcentaje formateado
  readonly formattedProgress = computed(() => `${this.progress()}%`);
  
  // Track function para el @for
  trackByIndex(index: number): number {
    return index;
  }
}
